import "./App.css";
import { lazy, Suspense } from "react";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { RestaurantAlerts } from "./components/RestaurantAlerts";
import { PrintStationWorker } from "./components/PrintStationWorker";
import Login from "./pages/Login";
import AppShell from "./components/layout/AppShell";
import StationGate from "./components/StationGate";
import LicenseLock from "./components/billing/LicenseLock";
const PagoResultado = lazy(() => import("./pages/PagoResultado"));
const Home = lazy(() => import("./pages/Home"));
const OrderTypes = lazy(() => import("./pages/pos/OrderTypes"));
const PosScreen = lazy(() => import("./pages/pos/PosScreen"));
const ProductsManager = lazy(() => import("./pages/ProductsManager"));
const Inventory = lazy(() => import("./pages/Inventory"));
const Configuration = lazy(() => import("./pages/Configuration"));
const Caja = lazy(() => import("./pages/Caja"));
const Pedidos = lazy(() => import("./pages/Pedidos"));
const Mesas = lazy(() => import("./pages/Mesas"));
const MapEditor = lazy(() => import("./pages/MapEditor"));
const KioskScreen = lazy(() => import("./pages/KioskScreen"));
const StaffManager = lazy(() => import("./pages/StaffManager"));
const WaiterTablet = lazy(() => import("./pages/WaiterTablet"));
const TabletSetup = lazy(() => import("./pages/TabletSetup"));
const TabletKiosk = lazy(() => import("./pages/TabletKiosk"));
const KdsScreen = lazy(() => import("./pages/KdsScreen"));
const PublicStore = lazy(() => import("./pages/PublicStore"));
const QrOrder = lazy(() => import("./pages/QrOrder"));
const ConciliacionPlataformas = lazy(() => import("./pages/ConciliacionPlataformas"));
const Clientes = lazy(() => import("./pages/Clientes"));
const PropinasPendientes = lazy(() => import("./pages/PropinasPendientes"));
const BancosCajas = lazy(() => import("./pages/BancosCajas"));
const ControlRepartidores = lazy(() => import("./pages/ControlRepartidores"));
const Historial = lazy(() => import("./pages/Historial"));
const Reportes = lazy(() => import("./pages/Reportes"));
const Propinas = lazy(() => import("./pages/Propinas"));
const Gastos = lazy(() => import("./pages/Gastos"));
const GastosFijos = lazy(() => import("./pages/GastosFijos"));
const Nomina = lazy(() => import("./pages/Nomina"));
const CustomerDisplay = lazy(() => import("./pages/CustomerDisplay"));
const Suscripcion = lazy(() => import("./pages/Suscripcion"));
const CajasCajeros = lazy(() => import("./pages/CajasCajeros"));
const MisNegocios = lazy(() => import("./pages/MisNegocios"));
const Facturacion = lazy(() => import("./pages/Facturacion"));
const AutoFactura = lazy(() => import("./pages/AutoFactura"));
const SaasDashboard = lazy(() => import("./pages/saas/SaasDashboard"));
import { InvoiceDialogHost } from "./components/invoicing/InvoiceDialogHost";
import { canSeeModule, modulesForUser, MODULE_ROUTE, planLocked } from "./lib/modules";
import { LockedModule } from "./components/LockedModule";

function Loader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
      <div className="w-10 h-10 border-4 border-[#1F2937] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function Protected({ children, roles, moduleKey, allowExpired }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.license_expired && user.role !== "superadmin" && !allowExpired) return <LicenseLock />;
  if (allowExpired) return roles && !roles.includes(user.role) ? <Navigate to="/app" replace /> : children;
  if (roles && !roles.includes(user.role)) {
    return <Navigate to={user.role === "superadmin" ? "/saas" : "/app"} replace />;
  }
  if (moduleKey && user.role !== "superadmin" && !canSeeModule(user, moduleKey)) {
    if (planLocked(user, moduleKey) && (!roles || roles.includes(user.role))) return <LockedModule moduleKey={moduleKey} />;
    const mods = modulesForUser(user);
    const firstRoute = mods && mods.length ? MODULE_ROUTE[mods[0]] : null;
    return <Navigate to={firstRoute || "/login"} replace />;
  }
  if (user.role !== "superadmin") {
    return <StationGate>{children}</StationGate>;
  }
  return children;
}

function Shell({ children }) {
  return <AppShell><Suspense fallback={<Loader />}>{children}</Suspense></AppShell>;
}

const RESTAURANT_ROLES = ["admin", "manager", "waiter", "cashier", "kitchen"];

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-right" richColors />
          <RestaurantAlerts />
          <PrintStationWorker />
          <InvoiceDialogHost />
          <Suspense fallback={<Loader />}>
          <Routes>
            <Route path="/login" element={<Login />} />

            <Route
              path="/saas"
              element={
                <Protected roles={["superadmin"]}>
                  <SaasDashboard />
                </Protected>
              }
            />

            <Route
              path="/app"
              element={
                <Protected roles={RESTAURANT_ROLES} moduleKey="inicio">
                  <Shell><Home /></Shell>
                </Protected>
              }
            />
            <Route
              path="/app/pedido"
              element={
                <Protected roles={["admin", "manager", "waiter", "cashier"]} moduleKey="pedido">
                  <Shell><OrderTypes /></Shell>
                </Protected>
              }
            />
            <Route
              path="/app/pedido/:type"
              element={
                <Protected roles={["admin", "manager", "waiter", "cashier"]} moduleKey="pedido">
                  <PosScreen />
                </Protected>
              }
            />
            <Route
              path="/app/kiosko"
              element={
                <Protected roles={["admin", "manager", "waiter", "cashier"]} moduleKey="pedido">
                  <KioskScreen />
                </Protected>
              }
            />
            <Route
              path="/app/mesas"
              element={
                <Protected roles={["admin", "manager", "waiter", "cashier"]} moduleKey="mesas">
                  <Shell><Mesas /></Shell>
                </Protected>
              }
            />
            <Route
              path="/app/configuracion/mapa"
              element={
                <Protected roles={["admin", "manager"]}>
                  <Shell><MapEditor /></Shell>
                </Protected>
              }
            />
            <Route
              path="/app/kds"
              element={
                <Protected roles={["admin", "manager", "kitchen"]} moduleKey="kds">
                  <Shell><KdsScreen /></Shell>
                </Protected>
              }
            />
            <Route
              path="/app/productos"
              element={
                <Protected roles={["admin", "manager"]} moduleKey="productos">
                  <Shell><ProductsManager /></Shell>
                </Protected>
              }
            />
            <Route
              path="/app/historial"
              element={
                <Protected roles={["admin", "manager", "cashier"]} moduleKey="historial">
                  <Shell><Historial /></Shell>
                </Protected>
              }
            />
            <Route
              path="/app/inventario"
              element={
                <Protected roles={["admin", "manager"]} moduleKey="inventario">
                  <Shell><Inventory /></Shell>
                </Protected>
              }
            />
            <Route
              path="/app/configuracion"
              element={
                <Protected roles={["admin", "manager"]} moduleKey="configuracion">
                  <Shell><Configuration /></Shell>
                </Protected>
              }
            />
            <Route
              path="/app/reportes"
              element={
                <Protected roles={["admin", "manager"]} moduleKey="reportes">
                  <Shell><Reportes /></Shell>
                </Protected>
              }
            />
            <Route
              path="/app/propinas"
              element={
                <Protected roles={["admin", "manager", "cashier"]} moduleKey="propinas">
                  <Shell><Propinas /></Shell>
                </Protected>
              }
            />
            <Route
              path="/app/gastos"
              element={
                <Protected roles={["admin", "manager"]} moduleKey="gastos">
                  <Shell><Gastos /></Shell>
                </Protected>
              }
            />
            <Route
              path="/app/gastos-fijos"
              element={
                <Protected roles={["admin", "manager"]} moduleKey="gastos-fijos">
                  <Shell><GastosFijos /></Shell>
                </Protected>
              }
            />
            <Route
              path="/app/nomina"
              element={
                <Protected roles={["admin", "manager"]} moduleKey="nomina">
                  <Shell><Nomina /></Shell>
                </Protected>
              }
            />
            <Route
              path="/app/caja"
              element={
                <Protected roles={["admin", "manager", "cashier"]} moduleKey="caja">
                  <Shell><Caja /></Shell>
                </Protected>
              }
            />
            <Route
              path="/app/pedidos"
              element={
                <Protected roles={["admin", "manager", "cashier", "waiter"]} moduleKey="pedidos">
                  <Shell><Pedidos /></Shell>
                </Protected>
              }
            />
            <Route
              path="/app/negocios"
              element={
                <Protected roles={["admin", "manager"]} moduleKey="multisucursal">
                  <Shell><MisNegocios /></Shell>
                </Protected>
              }
            />
            <Route
              path="/app/cajas"
              element={
                <Protected roles={["admin", "manager"]} moduleKey="cajas">
                  <Shell><CajasCajeros /></Shell>
                </Protected>
              }
            />
            <Route
              path="/app/bancos"
              element={
                <Protected roles={["admin", "manager", "cashier"]} moduleKey="bancos">
                  <Shell><BancosCajas /></Shell>
                </Protected>
              }
            />
            <Route
              path="/app/clientes"
              element={
                <Protected roles={["admin", "manager", "cashier"]} moduleKey="clientes">
                  <Shell><Clientes /></Shell>
                </Protected>
              }
            />
            <Route
              path="/app/conciliacion"
              element={
                <Protected roles={["admin", "manager"]} moduleKey="conciliacion">
                  <Shell><ConciliacionPlataformas /></Shell>
                </Protected>
              }
            />
            <Route
              path="/app/propinas-pendientes"
              element={
                <Protected roles={["admin", "manager", "waiter", "cashier"]} moduleKey="propinas-pendientes">
                  <Shell><PropinasPendientes /></Shell>
                </Protected>
              }
            />
            <Route
              path="/app/repartidores"
              element={
                <Protected roles={["admin", "manager", "cashier"]} moduleKey="repartidores">
                  <Shell><ControlRepartidores /></Shell>
                </Protected>
              }
            />
            <Route
              path="/app/usuarios"
              element={
                <Protected roles={["admin", "manager"]} moduleKey="usuarios">
                  <Shell><StaffManager /></Shell>
                </Protected>
              }
            />
            <Route
              path="/app/tablet"
              element={
                <Protected roles={["admin", "manager"]} moduleKey="tablet">
                  <Shell><TabletSetup /></Shell>
                </Protected>
              }
            />
            <Route
              path="/app/comandera"
              element={
                <Protected roles={["admin", "manager", "cashier", "waiter"]} moduleKey="comandera">
                  <Shell><WaiterTablet /></Shell>
                </Protected>
              }
            />

            <Route
              path="/app/pago"
              element={
                <Protected roles={["admin"]} allowExpired>
                  <PagoResultado />
                </Protected>
              }
            />

            <Route
              path="/app/facturacion"
              element={
                <Protected roles={["admin", "manager", "cashier"]} moduleKey="facturacion">
                  <Shell><Facturacion /></Shell>
                </Protected>
              }
            />

            <Route
              path="/app/suscripcion"
              element={
                <Protected roles={["admin"]}>
                  <Shell><Suscripcion /></Shell>
                </Protected>
              }
            />

            <Route
              path="/pantalla-cliente"
              element={
                <Protected roles={["admin", "manager", "cashier"]}>
                  <CustomerDisplay />
                </Protected>
              }
            />
            <Route path="/mesa-kiosko" element={<TabletKiosk />} />
            <Route path="/qr/:rid/:tableId" element={<QrOrder />} />
            <Route path="/factura/:slug" element={<AutoFactura />} />
            <Route path="/:slug" element={<PublicStore />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;
